import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { ADMIN, ADMINS, CUSTOMER, STAFF, USER } from "../../config/constants";
import socket, { APP_UPDATE_AVAILABLE } from "../../socket";
import { getFieldErrors, IFieldError, QueryStringBuilder } from "../helpers";
import { IAdmin } from "./adminsReducer";
import { IUser, IUser4Export, tryConfirmLogin } from "./authReducer";
import { ICustomer } from "./customersReducer";
import { IRole } from "./rolesReducer";
import { IStaff } from "./staffsReducer";

class RegUserErrs {
    form: string | null = null;

    role: string | null = null;
    specialRole: string | null = null;

    about: string | null = null;

    firstName: string | null = null;
    lastName: string | null = null;
    otherName: string | null = null;
    nickName: string | null = null;
    preferredName: string | null = null;
    dateOfBirth: string | null = null;
    gender: string | null = null;
    email: string | null = null;
    phone: string | null = null;
    mobile: string | null = null;
    password: string | null = null;
    usesEmail2FA: string | null = null;
    usesAuthenticator2FA: string | null = null;
}

class UpdateUserErrs {
    form: string | null = null;

    role: string | null = null;
    specialRole: string | null = null;

    about: string | null = null;
    usrImg: string | null = null;
    firstName: string | null = null;
    lastName: string | null = null;
    otherName: string | null = null;
    nickName: string | null = null;
    preferredName: string | null = null;
    dateOfBirth: string | null = null;
    gender: string | null = null;
    email: string | null = null;
    phone: string | null = null;
    mobile: string | null = null;
    oldPassword: string | null = null;      //current password
    password: string | null = null;     //new password
    usesEmail2FA: string | null = null;
    usesAuthenticator2FA: string | null = null;

    errCode: string | null = null;
}

export interface IUsersState {
    registeringUser: boolean;
    registeredUser: boolean;
    regUserErrs: RegUserErrs;
    fetchingUsers: boolean;
    fetchUsersErr: string | null;
    staffs: IUser[];
    customers: IUser[];
    admins: IUser[];
    fetchingUsers4Export: boolean;
    fetchedUsers4Export: boolean;
    fetchUsers4ExportErr: string | null;
    customers4Export: IUser4Export[];
    fetchingMainUser: boolean;
    fetchMainUserErr: string | null;
    mainUserId: string | null;
    mainUser: IAdmin | null;
    updatingUser: boolean,
    updatedUser: boolean,
    updateUserErrs: UpdateUserErrs,
    togglingUserStatus: boolean;
    toggleUserStatusErr: string | null;
    togglingSmeOwnerStatus: boolean;
    toggleSmeOwnerStatusErr: string | null;
    deletingUser: boolean;
    deletedUser: boolean;
    deleteUserErr: string | null;
}

const initialState = {
    fetchingUsers: false,
    fetchUsersErr: null,
    staffs: [],
    customers: [],
    admins: [],
    fetchingUsers4Export: false,
    fetchedUsers4Export: false,
    fetchUsers4ExportErr: null,
    customers4Export: [],
    registeringUser: false,
    registeredUser: false,
    regUserErrs: new RegUserErrs,
    fetchingMainUser: false,
    fetchMainUserErr: null,
    mainUserId: null,
    mainUser: null,
    updatingUser: false,
    updatedUser: false,
    updateUserErrs: new UpdateUserErrs(),
    togglingUserStatus: false,
    toggleUserStatusErr: null,
    togglingSmeOwnerStatus: false,
    toggleSmeOwnerStatusErr: null,
    deletingUser: false,
    deletedUser: false,
    deleteUserErr: null
} as IUsersState;

const usersSlice = createSlice({
    "name": "users",
    initialState,
    reducers: {
        clearRegUserFieldErr: (state: IUsersState, action: PayloadAction<string>) => {
            state.regUserErrs[action.payload as keyof RegUserErrs] = null;
        },
        clearRegUserErrs: (state: IUsersState) => {
            state.regUserErrs = new RegUserErrs();
        },
        tryResetRegUserFormErr: (state: IUsersState) => {
            let shouldReset = true;

            for (let key in state.regUserErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.regUserErrs[key as keyof RegUserErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.regUserErrs.form = null;
            }
        },
        resetRegisteredUser: (state: IUsersState) => {
            state.registeredUser = false;
        },
        regUserStart: (state: IUsersState) => {
            state.registeredUser = false;
            state.regUserErrs = new RegUserErrs();
            state.registeringUser = true;
        },
        regUserPass: (state: IUsersState) => {
            state.registeredUser = true;
            state.registeringUser = false;
        },
        regUserFail: (state: IUsersState, action: PayloadAction<IFieldError[]>) => {
            state.registeringUser = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.regUserErrs) {
                    if (err.field === key) {
                        state.regUserErrs[key as keyof RegUserErrs] = err.msg;
                        break;
                    }
                }
            }

            if (!state.regUserErrs.form) {
                state.regUserErrs.form = 'Form incorrectly filled.';
            }
        },
        fetchUsersStart: (state: IUsersState) => {
            state.fetchingUsers = true;
        },
        fetchUsersPass: (state: IUsersState, action: PayloadAction<{ users: IUser[], role: string }>) => {
            const { users, role } = action.payload;

            state.fetchingUsers = false;

            switch (role) {
                case CUSTOMER:
                    state.customers = users;
                    break;
                case ADMIN:
                    state.admins = users;
                    break;
                case STAFF:
                    state.staffs = users;
                    break;
                default:
                    break;
            }

        },
        fetchUsersFail: (state: IUsersState, action: PayloadAction<string>) => {
            state.fetchingUsers = false;
            state.fetchUsersErr = action.payload;
        },
        fetchUsers4ExportStart: (state: IUsersState) => {
            state.fetchingUsers4Export = true;
            state.fetchedUsers4Export = false;
        },
        fetchUsers4ExportPass: (state: IUsersState, action: PayloadAction<{ users: IUser4Export[], role: string }>) => {
            const { users, role } = action.payload;
            switch (role) {
                case CUSTOMER:
                    state.customers4Export = users
                    break;
                case ADMIN:
                    break;
                case STAFF:
                    break;
                default:
                    break;
            }

            state.fetchingUsers4Export = false;
            state.fetchedUsers4Export = true;

        },
        resetFetchedUsers4Export: (state: IUsersState) => {
            state.fetchedUsers4Export = false;
        },
        fetchUsers4ExportFail: (state: IUsersState, action: PayloadAction<string>) => {
            state.fetchingUsers4Export = false;
            state.fetchUsers4ExportErr = action.payload;
        },
        clearFetchUsers4ExportErr: (state: IUsersState) => {
            state.fetchUsers4ExportErr = null;
        },
        clearFetchMainUserErr: (state: IUsersState) => {
            state.fetchMainUserErr = null;
        },
        fetchMainUserStart: (state: IUsersState) => {
            state.fetchingMainUser = true;
            state.fetchMainUserErr = null;
        },
        fetchMainUserPass: (state: IUsersState, action: PayloadAction<IAdmin>) => {
            state.fetchingMainUser = false;
            state.mainUser = action.payload;

        },
        fetchMainUserFail: (state: IUsersState, action: PayloadAction<string>) => {
            state.fetchingMainUser = false;
            state.fetchMainUserErr = action.payload;
        },
        setMainUserId: (state: IUsersState, action: PayloadAction<string | null>) => {
            state.mainUserId = action.payload;
        },
        setMainUser: (state: IUsersState, action: PayloadAction<IAdmin | null>) => {
            state.mainUser = action.payload;
        },
        clearUpdateUserFieldErr: (state: IUsersState, action: PayloadAction<string>) => {
            state.updateUserErrs[action.payload as keyof UpdateUserErrs] = null;
        },
        clearUpdateUserErrs: (state: IUsersState) => {
            state.updateUserErrs = new UpdateUserErrs();
        },
        tryResetUpdateUserFormErr: (state: IUsersState) => {
            let shouldReset = true;

            for (let key in state.updateUserErrs) {

                if (key === 'form') {
                    continue;
                }

                if (state.updateUserErrs[key as keyof UpdateUserErrs]) {
                    shouldReset = false;
                    break;
                }
            }

            if (shouldReset) {
                state.updateUserErrs.form = null;
            }
        },
        resetUpdatedUser: (state: IUsersState) => {
            state.updatedUser = false;
        },
        updateUserStart: (state: IUsersState) => {
            state.updatingUser = true;
            state.updatedUser = false;
            state.updateUserErrs = new UpdateUserErrs();
        },
        updateUserPass: (state: IUsersState) => {
            state.updatingUser = false;
            state.updatedUser = true;
        },
        updateUserFail: (state: IUsersState, action: PayloadAction<IFieldError[]>) => {
            state.updatingUser = false;

            const errs = action.payload;

            for (const err of errs) {
                for (const key in state.updateUserErrs) {
                    if (err.field === key) {
                        state.updateUserErrs[key as keyof UpdateUserErrs] = err.msg;
                        break;
                    }
                }
            }


            if (!state.updateUserErrs.form) {
                state.updateUserErrs.form = 'Form incorrectly filled.';
            }
        },
        cleartoggleUserStatusErr: (state: IUsersState) => {
            state.toggleUserStatusErr = null;
        },
        toggleUserStatusStart: (state: IUsersState) => {
            state.togglingUserStatus = true;
            state.toggleUserStatusErr = null;
        },
        toggleUserStatusPass: (state: IUsersState) => {
            state.togglingUserStatus = false;
        },
        toggleUserStatusFail: (state: IUsersState, action: PayloadAction<string>) => {
            state.togglingUserStatus = false;
            state.toggleUserStatusErr = action.payload
        },
        cleartoggleSmeOwnerStatusErr: (state: IUsersState) => {
            state.toggleSmeOwnerStatusErr = null;
        },
        toggleSmeOwnerStatusStart: (state: IUsersState) => {
            state.togglingSmeOwnerStatus = true;
            state.toggleSmeOwnerStatusErr = null;
        },
        toggleSmeOwnerStatusPass: (state: IUsersState) => {
            state.togglingSmeOwnerStatus = false;
        },
        toggleSmeOwnerStatusFail: (state: IUsersState, action: PayloadAction<string>) => {
            state.togglingSmeOwnerStatus = false;
            state.toggleSmeOwnerStatusErr = action.payload
        },
        clearDeletUserErr: (state: IUsersState) => {
            state.deleteUserErr = null;
        },
        resetDeletedUser: (state: IUsersState) => {
            state.deletedUser = false;
        },
        deleteUserStart: (state: IUsersState) => {
            state.deleteUserErr = null;
            state.deletedUser = false;
            state.deletingUser = true;
        },
        deleteUserPass: (state: IUsersState) => {
            state.deletedUser = true;
            state.deletingUser = false;
        },
        deleteUserFail: (state: IUsersState, action: PayloadAction<string>) => {
            state.deleteUserErr = action.payload;
            state.deletingUser = false;
        }
    }
});

export const {
    clearRegUserFieldErr,
    clearRegUserErrs,
    tryResetRegUserFormErr,
    resetRegisteredUser,
    regUserStart,
    regUserPass,
    regUserFail,
    clearFetchMainUserErr,
    fetchMainUserStart,
    fetchMainUserPass,
    fetchMainUserFail,
    setMainUser,
    setMainUserId,
    fetchUsersStart,
    fetchUsersPass,
    fetchUsersFail,
    fetchUsers4ExportStart,
    fetchUsers4ExportPass,
    resetFetchedUsers4Export,
    fetchUsers4ExportFail,
    clearFetchUsers4ExportErr,
    clearUpdateUserFieldErr,
    clearUpdateUserErrs,
    tryResetUpdateUserFormErr,
    resetUpdatedUser,
    updateUserStart,
    updateUserPass,
    updateUserFail,
    cleartoggleUserStatusErr,
    toggleUserStatusStart,
    toggleUserStatusPass,
    toggleUserStatusFail,
    toggleSmeOwnerStatusStart,
    toggleSmeOwnerStatusPass,
    toggleSmeOwnerStatusFail,
    clearDeletUserErr,
    resetDeletedUser,
    deleteUserStart,
    deleteUserPass,
    deleteUserFail
} = usersSlice.actions;

interface ITryRegUserArg {
    authToken: string;
    data: {
        role: string;
        firstName: string;
        lastName: string;
        about?: string;
        email: string;
        phone: string;
        password?: string;

        specialRole?: string;
        otherName?: string;
        nickName?: string;
        preferredName?: string;
        dateOfBirth?: string;
        gender?: string;
        mobile?: string;
        usesEmail2FA?: boolean;
        usesAuthenticator2FA?: boolean;
    }
}


export const tryRegUser = createAsyncThunk("auth/tryRegUser", (arg: ITryRegUserArg, { dispatch }) => {
    const { data, authToken } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/user`;

    dispatch(regUserStart());
    axios.post(endpoint, data, {
        headers: {
            "Content-Type": "application/json",
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(regUserPass());
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data && data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data && data?.errMsg) {
            fieldErrors = [{ field: 'form', msg: data.errMsg }];

        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(regUserFail(fieldErrors));
    });
});

interface ITryfetchUsersArg {
    authToken: string;
    role: string;
    smeOwnerStatus?: string;
}

export const tryFetchUsers = createAsyncThunk('users/tryFetchUsers', (arg: ITryfetchUsersArg, { dispatch }) => {

    const { authToken, role, smeOwnerStatus } = arg;

    const queryBuilder = new QueryStringBuilder();

    queryBuilder.addInput('role', role);

    if (smeOwnerStatus) {
        queryBuilder.addInput('smeOwnerStatus', smeOwnerStatus);
    }

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/users${queryBuilder.getString()}`;




    dispatch(fetchUsersStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchUsersPass({ users: res.data.users, role }));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(fetchUsersFail(data.err));
        } else {
            dispatch(fetchUsersFail('Check your network connection.'))
        }

    });
});

interface ITryfetchUsers4ExportArg {
    authToken: string;
    role: string;
    smeOwnerStatus?: string;
    emailVerificationStatus?: string;
    activeMembershipType?: string;
}

export const tryFetchUsers4Export = createAsyncThunk('users/tryFetchUsers4Export', (arg: ITryfetchUsers4ExportArg, { dispatch }) => {

    const { authToken, role, smeOwnerStatus, emailVerificationStatus, activeMembershipType } = arg;

    const queryBuilder = new QueryStringBuilder();

    queryBuilder.addInput('role', role);

    if (smeOwnerStatus) {
        queryBuilder.addInput('smeOwnerStatus', smeOwnerStatus);
    }

    if (emailVerificationStatus) {
        queryBuilder.addInput('emailVerificationStatus', emailVerificationStatus);
    }

    if (activeMembershipType) {
        queryBuilder.addInput('activeMembershipType', activeMembershipType);
    }

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/users-4-export${queryBuilder.getString()}`;


    dispatch(fetchUsers4ExportStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(fetchUsers4ExportPass({ users: res.data, role }));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(fetchUsers4ExportFail(data.err));
        } else {
            dispatch(fetchUsers4ExportFail('Check your network connection.'))
        }

    });
});


interface ITryFetchMainUserArg {
    authToken: string;
    userId: string;
}

export const tryFetchMainUser = createAsyncThunk('users/tryFetchMainUser', (arg: ITryFetchMainUserArg, { dispatch }) => {
    const { authToken, userId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/users/${userId}`;

    dispatch(fetchMainUserStart());
    axios.get(endpoint, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        const data = res.data;
        dispatch(fetchMainUserPass(res.data));
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.errors) {
            dispatch(fetchMainUserFail(getFieldErrors(data.errors)[0].msg));
        } else if (data?.err) {
            dispatch(fetchMainUserFail(data.err));
        } else {
            dispatch(fetchMainUserFail('Check your network connection.'));
        }
    });
});


interface ITryUpdateUserArg {
    userId: string;
    data: FormData;
    authToken: string;
}

export const tryUpdateUser = createAsyncThunk('users/tryUpdateUser', (arg: ITryUpdateUserArg, { dispatch }) => {

    const { userId, data, authToken } = arg;


    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/users/${userId}`;

    dispatch(updateUserStart());

    axios.put(endpoint, data, {
        headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(updateUserPass(res.data));
    }).catch(err => {

        const data = err?.response?.data;

        let fieldErrors: IFieldError[] = [];

        if (data?.errors) {
            fieldErrors = getFieldErrors(data.errors);
        } else if (data?.errMsg) {
            fieldErrors = [{ field: 'form', msg: data.errMsg }];
        } else {
            fieldErrors = [{ field: 'form', msg: 'Check your network connection.' }];
        }

        dispatch(updateUserFail(fieldErrors));
    })
});

interface ITryToggleUserStatusArg {
    userId: string;
    authToken: string;
    setToggled: (truth: boolean) => void;
}

export const tryToggleUserStatus = createAsyncThunk('users/toggleUserStatus', (arg: ITryToggleUserStatusArg, { dispatch }) => {
    const { authToken, userId, setToggled } = arg;


    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/users/${userId}/toggle-status`;

    dispatch(toggleUserStatusStart());

    axios.post(endpoint, {}, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(toggleUserStatusPass());
        setToggled(true);
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(toggleUserStatusFail(data.err))
        } else {
            dispatch(toggleUserStatusFail('Check your internet connection.'));
        }
    })
});

interface ITryToggleSmeOwnerStatusArg {
    userId: string;
    authToken: string;
    setToggled: (truth: boolean) => void;
}

export const tryToggleSmeOwnerStatus = createAsyncThunk('users/toggleSmeOwnerStatus', (arg: ITryToggleSmeOwnerStatusArg, { dispatch }) => {
    const { authToken, userId, setToggled } = arg;


    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/users/${userId}/toggle-sme-owner-status`;

    dispatch(toggleSmeOwnerStatusStart());

    axios.post(endpoint, {}, {
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
        }
    }).then(res => {
        dispatch(toggleSmeOwnerStatusPass());
        setToggled(true);
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }

        if (data?.err) {
            dispatch(toggleSmeOwnerStatusFail(data.err))
        } else {
            dispatch(toggleSmeOwnerStatusFail('Check your internet connection.'));
        }
    })
});

interface IDeleteUserArg {
    authToken: string;
    userId: string;
}

export const tryDeleteUser = createAsyncThunk('users/deleteUser', (arg: IDeleteUserArg, { dispatch }) => {
    const { authToken, userId } = arg;

    const endpoint = `${process.env.REACT_APP_SERVER_ENDPOINT}/api/users/${userId}`;

    dispatch(deleteUserStart())
    axios.delete(endpoint, {
        headers: {
            'Authorization': `Bearer ${authToken}`,
            'Content-Type': 'application/json'
        }
    }).then(res => {
        dispatch(deleteUserPass());
        socket.emit(APP_UPDATE_AVAILABLE, USER);
    }).catch(err => {
        const data = err?.response?.data;

        if (data?.err && (data.err === "Session expired")) {
            dispatch(tryConfirmLogin({ authToken }));
            return;
        }
        let error = '';

        if (data?.err) {
            error = data.err;
        } else if (data?.errors) {
            error = getFieldErrors(data.errors)[0].msg;
        } else {
            error = 'Check your network connection.';
        }

        dispatch(deleteUserFail(error));
    });
});

export default usersSlice.reducer;